import styled from 'styled-components';
import { screenMd } from 'constants/styles/breakpoints';
import { TeamPickerInput } from 'pages/Users/components/InviteUsers/AddByLink/styles';
import { colors } from 'constants/styles/colors';

// The first step: existing users, an "or" divider, then new emails.
export const SelectStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 0 10px;
  ${screenMd} {
    padding: 0;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  line-height: 26px;
  color: ${colors.dataVisualization.black.dark};
  & + div {
    margin-top: 15px;
  }
`;

export const SectionDescription = styled.p`
  margin: 3px 0 12px 0;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
`;

export const SectionHelper = styled.p`
  margin: 12px 0 0 0;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
`;

export const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  font-size: 16px;
  line-height: 24px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
  &::before,
  &::after {
    content: '';
    flex: 1;
    border-top: 1px solid ${colors.palette.Business_LightThemeGreyLighter};
  }
`;

// 14px placeholder; typed text uses the field's 12px.
export const UserPickerInput = styled(TeamPickerInput)`
  &::placeholder {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const OutcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 10px;
  ${screenMd} {
    padding: 0;
  }
`;

export const OutcomeTitle = styled(SectionTitle)`
  /* Focused from code when the outcome shows, not by the keyboard. */
  &:focus {
    outline: none;
  }
`;

export const OutcomeIntro = styled.p`
  margin: -12px 0 0 0;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
`;

export const OutcomeSectionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  color: ${colors.fonts.dark};
`;

export const OutcomeList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 10px;
  border-radius: 10px;
  background: ${colors.palette.Business_LightThemeGreyLighter};
  list-style: none;
`;

export const OutcomeRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px;
  border-radius: 10px;
  background: ${colors.fonts.white};
`;

export const OutcomeUser = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  > b {
    color: ${colors.fonts.dark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > span {
    color: ${colors.palette.Business_LightThemeGreyDarker};
  }
  > ul {
    margin-top: 6px;
  }
`;

export const OutcomeLine = styled.li<{ $failed?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: ${({ $failed }) => ($failed ? colors.performance.negative : colors.fonts.dark)};
  > svg {
    flex-shrink: 0;
    /* Level with the first line of text. */
    margin-top: 2px;
  }
`;

export const OutcomeActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
  padding: 0 16px;
  ${screenMd} {
    padding: 0;
  }
`;

export const OutcomeLines = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const OutcomeLineText = styled.span`
  display: flex;
  flex-direction: column;
`;

export const OutcomeReason = styled.span`
  color: ${colors.palette.Business_LightThemeGreyDarker};
`;

// An unstyled fieldset that disables a step's controls during a request.
export const StepFieldset = styled.fieldset`
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

// Lays the access step out as if the fieldset weren't there, so the modal's height limit reaches
// the access table. A flex fieldset doesn't pass it on: Chrome wraps its content in a box of its
// own. The fieldset still disables the rows while saving.
export const AccessStepFieldset = styled(StepFieldset)`
  ${screenMd} {
    display: contents;
  }
`;
